import {
  Base,
  Extent,
  Feature,
  Geometry,
  Layer,
  LineString,
  Map,
  OSM,
  Point,
  Polygon,
  Projection,
  Snap,
  TileLayer,
  VectorLayer
} from '../../models'

import {v4 as uuidv4} from 'uuid'
import appConfig from '../../../reducers/appConfig'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {basemapHelper, mapToBlob} from '../../helpers'
import {IGeometryEditsDispatcher, IMapDispatcher} from '../../interfaces/dispatchers'
import {IMapClickParams} from '../../interfaces/models'
import {IConfig} from '../../interfaces/reducers'
import {IMapDrawingService, IMapService} from '../../interfaces/services'
import {IMapEditingService} from '../../interfaces/services/IMapEditingService'
import {MapDrawingService} from './MapDrawingService'
import {MapEditingService} from './MapEditingService'

interface OlClickEventParams {
  coordinate: [number, number]
  pixel: [number, number]
  originalEvent: MouseEvent
  type: string
}
export class MapService implements IMapService {
  public map: Map
  public mapDiv: HTMLElement
  public drawing: IMapDrawingService
  public editing: IMapEditingService
  protected config: IConfig
  mapDispatcher: IMapDispatcher
  constructor(
    config: IConfig,
    map: Map,
    geomDispatcher: IGeometryEditsDispatcher,
    mapDispatcher: IMapDispatcher,
    mapDiv: HTMLElement
  ) {
    this.map = map
    this.config = config
    this.mapDiv = mapDiv
    this.mapDispatcher = mapDispatcher
    this.drawing = new MapDrawingService(config, map, this)
    this.editing = new MapEditingService(config, map, geomDispatcher, this)
    document.onkeyup = (ev: KeyboardEvent) => {
      ev = ev || (window.event as KeyboardEvent)
      let isEscape = false
      if (ev['key']) {
        isEscape = ev.key === 'Escape' || ev.key === 'Esc'
      } else {
        isEscape = ev.keyCode === 27
      }
      if (isEscape) {
        this.drawing.deactivateDraw(false)
      }
    }
  }
  lockId: string
  onStartInteraction = (lockId: string) => {
    if (!this.lockId) {
      this.lockId = lockId
      this.stopCursorPointer()
      this.stopClickEvent()

      this.addSnaps()
    }
  }
  onEndInteraction = (lockId: string) => {
    if (this.lockId && this.lockId == lockId) {
      this.lockId = ''
      this.removeSnaps()
    }
  }
  trackingCursor = false
  public startCursorPointer() {
    if (!this.trackingCursor) {
      this.trackingCursor = true
    }
  }
  clickEvent: any
  mapOnClick = (event: any) => {
    const e = event as OlClickEventParams
    const mapClickProps: IMapClickParams = {
      mapPoint: new Point(e.coordinate),
      x: e.pixel[0],
      y: e.pixel[1],
      type: e.type,
      timestamp: new Date().getTime(),
      stopPropagation: () => {},
      button: e.originalEvent.button,
      buttons: e.originalEvent.buttons
    }

    this.mapDispatcher.onMapClick(mapClickProps)
  }
  startClickEvent() {
    this.clickEvent = this.map.on('click', this.mapOnClick)
  }
  stopClickEvent() {
    this.clickEvent = this.map.un('click', this.mapOnClick)
  }

  public stopCursorPointer() {
    this.trackingCursor = false
    this.mapDiv.style.cursor = 'default'
  }

  public setBasemap(key: string): void {
    const layers = this.getBasemapLayers()
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i]
      this.removeLayer(layer)
    }
    if (key == 'empty') {
      this.mapDiv.style.pointerEvents = 'unset'
      return
    }
    if (basemapHelper.getGoogleBasemaps().indexOf(key) == -1) {
      this.mapDiv.style.pointerEvents = 'unset'
      const basemapLayer = this.getBasemap(key)
      this.addLayer(basemapLayer)
    } else {
      const basemapLayer = this.getBasemap(key)
      this.addLayer(basemapLayer)
    }
  }

  public getResolution(): number {
    return this.map.getView().getResolution() || 1
  }

  protected getBasemap(key: string): Layer {
    let basemap: Layer
    if (basemapHelper.getEsriBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getEsriBasemap(key)
    } else if (basemapHelper.getOsmBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getOsmBasemap(key)
    } else if (basemapHelper.getWindyBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getWindyBasemap(key)
    } else if (basemapHelper.getCartodbBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getCartodbBasemap(key)
    } else if (basemapHelper.getStamenBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getStamenBasemap(key)
    } else if (basemapHelper.getGoogleBasemaps().indexOf(key) != -1) {
      basemap = basemapHelper.getGoogleBasemap(key)
    } else if (basemapHelper.ownMaps().indexOf(key) != -1) {
      basemap = basemapHelper.getOwnBasemap(key)
    } else if (basemapHelper.pupMaps().indexOf(key) != -1) {
      basemap = basemapHelper.getOwnBasemap(key)
    } else {
      basemap = new TileLayer({
        source: new OSM()
      })
    }
    basemap.setZIndex(0)
    basemap.moreInfo = {
      refreshOnExtentChange: false,
      urls: [],
      guid: uuidv4(),
      type: 'basemap',
      isActive: true
    } as any

    return basemap
  }
  public getPixelsFromCoordinates(coordinates: number[]): number[] {
    return this.map.getPixelFromCoordinate(coordinates)
  }
  public getExtent(): Extent {
    return new Extent(this.map.getView().calculateExtent())
  }
  public getCode(): CoordinateSystem {
    return this.map.getView().getProjection().getCode() as CoordinateSystem
  }
  public getSRID(): number {
    return parseInt(this.map.getView().getProjection().getCode().replace('EPSG:', ''))
  }
  public getWidth(): number {
    return (this.map.getSize() || [1])[0]
  }
  public getHeight(): number {
    return (this.map.getSize() || [1, 1])[1]
  }
  public getContainer() {
    return this.map.getTarget() as HTMLDivElement
  }
  public getCenter(): number[] {
    return this.map.getView().getCenter() || []
  }
  public getZoom(): number {
    return this.map.getView().getZoom() || 1
  }

  public on(key: string, callback: (event: any) => void) {
    this.map.on(key as any, callback)
  }
  public setZoom(zoom: number) {
    this.map.getView().setZoom(zoom)
  }
  public setCenter(center: number[]) {
    this.map.getView().setCenter(center)
  }

  public export(): Promise<Blob> {
    return mapToBlob(this.map)
  }

  public getLayers(): Base[] {
    return (this.map.getLayers().getArray() as Base[]).filter(l => l.moreInfo.type == 'regular')
  }

  public getDrawingLayers(): Base[] {
    return (this.map.getLayers().getArray() as Base[]).filter(l => l.moreInfo.type == 'draw')
  }

  public getBasemapLayers(): Base[] {
    return (this.map.getLayers().getArray() as Base[]).filter(l => l.moreInfo.type == 'basemap')
  }

  protected pointToExtent(x: number, y: number): [number, number, number, number] {
    const length = 1000
    return [x - length / 2, y - length / 2, x + length / 2, y + length / 2]
  }
  protected polylineToExtent(polyline: LineString): Array<number> {
    let extent: number[] = [] as number[]
    ;(polyline.getCoordinates() as any).forEach((path: any) => {
      path.forEach((point: number[]) => {
        if (extent.length == 0) {
          extent = this.pointToExtent(point[0], point[1])
        } else {
          extent = [
            extent[0] > point[0] ? point[0] : extent[0],
            extent[0] > point[0] ? point[0] : extent[0],
            extent[0] < point[0] ? point[1] : extent[1],
            extent[0] < point[0] ? point[1] : extent[1]
          ]
        }
      })
    })

    return extent
  }
  protected polygonToExtent(polygon: Polygon): [number, number, number, number] {
    let extent: [number, number, number, number]
    polygon.getCoordinates().forEach(ring => {
      ring.forEach((poly: number[]) => {
        poly.forEach(point => {
          if (!extent) {
            extent = [(point as any)[0], (point as any)[1], (point as any)[0], (point as any)[1]]
          } else {
            extent = [
              extent[0] > (point as any)[0] ? (point as any)[0] : extent[0],
              extent[1] > (point as any)[1] ? (point as any)[1] : extent[1],
              extent[2] < (point as any)[0] ? (point as any)[0] : extent[2],
              extent[3] < (point as any)[1] ? (point as any)[1] : extent[3]
            ]
          }
        })
      })
    })
    return extent!
  }
  protected joinExtents(extents: Array<Array<number>>): [number, number, number, number] {
    let extent: [number, number, number, number]
    extents.forEach((ex: [number, number, number, number]) => {
      if (!extent) {
        extent = ex
      } else {
        extent = [
          Math.min(extent[0], ex[0]),
          Math.min(extent[1], ex[1]),
          Math.max(extent[2], ex[2]),
          Math.max(extent[3], ex[3])
        ]
      }
    })
    return extent!
  }
  public animateTo(point: Point, zoomLevel?: number, duration?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      duration = duration || 500
      this.map.getView().animate({
        center: point.getCoordinates(),
        zoom: zoomLevel || this.map.getView().getZoom(),
        duration: duration || 500
      })
      setTimeout(() => {
        resolve()
      }, duration)
    })
  }
  public zoomTo(geometries: Array<Geometry>, time?: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const extents: Array<Array<number>> = []
      geometries.forEach(g => {
        extents.push(g.getExtent())
      })
      const extent = this.joinExtents(extents)
      if (extent[2] - extent[0] < 100) {
        extent[0] = extent[0] - 100
        extent[2] = extent[2] + 100
      }
      if (!time) {
        time = 200
      }
      ;(this.map.getView().fit as any)(extent, {duration: time})
      setTimeout(() => {
        resolve()
      }, time)
    })
  }

  public panTo(geometry: Geometry): Promise<void> {
    return new Promise((resolve, reject) => {
      const time = 200
      const point = geometry.getCenter()
      const line = new LineString([
        [point.getCoordinates()[0], point.getCoordinates()[1]],
        [(this.map.getView().getCenter() || [1])[0], (this.map.getView().getCenter() || [1, 1])[1]]
      ])
      this.map.getView().animate({
        center: point.getCoordinates(),
        duration: time
      })
      setTimeout(() => {
        resolve()
      }, time)
    })
  }

  public updateSize(): void {
    this.map.dispatchEvent('ext')
    this.map.updateSize()
  }
  snapInteractions: {[layerId: string]: Snap} = {}
  public addLayer(layer: Layer): void {
    this.map.addLayer(layer)
    if (layer instanceof VectorLayer && layer.moreInfo.urls[0] != appConfig.reg.measures.url) {
      const snap = new Snap({source: layer.getSource() as any})
      this.snapInteractions[layer.moreInfo.urls[0]] = snap
    }
  }

  public addSnaps(filter?: {[layerId: string]: any}) {
    if (filter) {
      for (const layerId in filter) {
        if (this.snapInteractions[layerId]) {
          this.map.addInteraction(this.snapInteractions[layerId])
        }
      }
    } else {
      for (const layerId in this.snapInteractions) {
        this.map.addInteraction(this.snapInteractions[layerId])
      }
    }
  }

  public removeSnaps(filter?: {[layerId: string]: any}) {
    if (filter) {
      for (const layerId in filter) {
        if (this.snapInteractions[layerId]) {
          this.map.removeInteraction(this.snapInteractions[layerId])
        }
      }
    } else {
      for (const layerId in this.snapInteractions) {
        this.map.removeInteraction(this.snapInteractions[layerId])
      }
    }
  }

  public removeLayer(layer: Layer): void {
    this.map.removeLayer(layer)
  }

  public getProjection(): Projection {
    return this.map.getView().getProjection()
  }
  public getPixelFromCoordinate(coordinate: number[]): number[] {
    return this.map.getPixelFromCoordinate(coordinate)
  }

  public forEachFeatureAtPixel(pixel: [number, number], callback: (f: Feature, layer: Layer) => void): void {
    return this.map.forEachFeatureAtPixel(pixel, callback as any)
  }
}
